import React, { useEffect, useState } from 'react';
import GitHubIcon from '@mui/icons-material/GitHub';
import StarIcon from '@mui/icons-material/Star';
import ForkRightIcon from '@mui/icons-material/ForkRight';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PeopleIcon from '@mui/icons-material/People';
import CommitIcon from '@mui/icons-material/Commit';
import { motion } from 'framer-motion';
import setup from '../assets/data/setup.json';
import MotionSection from './ui/MotionSection';
import SectionHeader from './ui/SectionHeader';

interface GitHubRepo {
  name: string;
  description: string | null;
  html_url: string;
  stargazers_count: number;
  forks_count: number;
  language: string | null;
  updated_at?: string;
}

interface GitHubUser {
  public_repos: number;
  followers: number;
  following?: number;
  avatar_url?: string;
  html_url: string;
}

interface ContributionDay {
  date: string;
  count: number;
  level: number;
}

interface GitHubData {
  user: GitHubUser;
  repos: GitHubRepo[];
  totalStars: number;
  contributions: ContributionDay[];
  totalContributions: number;
  languageBreakdown: { language: string; count: number; percent: number }[];
  fromCache?: boolean;
}

type ActivityRepo = {
  name: string;
  description: string | null;
  html_url: string;
  language: string | null;
  stars: number;
  forks: number;
  pushed_at?: string;
};

type GitHubActivity = {
  syncedAt?: string;
  username?: string;
  profileUrl?: string;
  publicRepos?: number;
  followers?: number;
  recentRepos?: ActivityRepo[];
  recentCommits?: { repo: string; message: string; date: string | null; url: string }[];
  languages?: { language: string; repos: number }[];
};

const levelColors = [
  'bg-gray-100 dark:bg-white/[0.04]',
  'bg-emerald-200 dark:bg-emerald-900/60',
  'bg-emerald-400 dark:bg-emerald-700/80',
  'bg-emerald-500 dark:bg-emerald-600',
  'bg-emerald-600 dark:bg-emerald-500',
];

function buildLanguageBreakdown(
  repos: { language: string | null }[],
  fallbackLanguages?: { language: string; repos: number }[]
) {
  if (fallbackLanguages?.length) {
    const total = fallbackLanguages.reduce((sum, l) => sum + l.repos, 0) || 1;
    return fallbackLanguages.map((l) => ({
      language: l.language,
      count: l.repos,
      percent: Math.round((l.repos / total) * 100),
    }));
  }

  const languageCounts: Record<string, number> = {};
  repos.forEach((r) => {
    if (r.language) languageCounts[r.language] = (languageCounts[r.language] || 0) + 1;
  });
  const total = repos.length || 1;
  return Object.entries(languageCounts)
    .sort(([, a], [, b]) => b - a)
    .map(([language, count]) => ({
      language,
      count,
      percent: Math.round((count / total) * 100),
    }));
}

function dataFromActivity(activity: GitHubActivity | undefined, username: string): GitHubData | null {
  if (!activity?.recentRepos?.length && !activity?.publicRepos) return null;

  const repos: GitHubRepo[] = (activity.recentRepos || []).slice(0, 6).map((r) => ({
    name: r.name,
    description: r.description,
    html_url: r.html_url,
    stargazers_count: r.stars || 0,
    forks_count: r.forks || 0,
    language: r.language,
    updated_at: r.pushed_at,
  }));

  return {
    user: {
      public_repos: activity.publicRepos || repos.length,
      followers: activity.followers || 0,
      html_url: activity.profileUrl || `https://github.com/${username}`,
    },
    repos,
    totalStars: repos.reduce((sum, r) => sum + r.stargazers_count, 0),
    contributions: [],
    totalContributions: activity.recentCommits?.length || 0,
    languageBreakdown: buildLanguageBreakdown(repos, activity.languages),
    fromCache: true,
  };
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="ui-card p-5">
      <div className="flex items-center gap-2 text-ink-muted mb-2">
        {icon}
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">{label}</span>
      </div>
      <p className="text-2xl font-semibold text-ink tabular-nums tracking-tight">{value}</p>
    </div>
  );
}

function ContributionHeatmap({ contributions }: { contributions: ContributionDay[] }) {
  const weeks: ContributionDay[][] = [];
  let currentWeek: ContributionDay[] = [];

  contributions.forEach((day, i) => {
    const dayOfWeek = new Date(day.date).getDay();
    if (i === 0 && dayOfWeek !== 0) {
      for (let j = 0; j < dayOfWeek; j++) {
        currentWeek.push({ date: '', count: 0, level: 0 });
      }
    }
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });
  if (currentWeek.length) weeks.push(currentWeek);

  return (
    <div className="ui-card p-5 sm:p-6 overflow-x-auto">
      <p className="ui-eyebrow mb-4">Contribution Activity</p>
      <div className="flex gap-[3px] min-w-fit">
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-[3px]">
            {week.map((day, di) => (
              <div
                key={day.date || `empty-${wi}-${di}`}
                title={day.date ? `${day.count} contributions on ${day.date}` : undefined}
                className={`w-[11px] h-[11px] rounded-sm ${levelColors[day.level] ?? levelColors[0]}`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function GitHubSection() {
  const [data, setData] = useState<GitHubData | null>(null);
  const [loading, setLoading] = useState(true);
  const username = setup.github.username;
  const activity = (setup as { githubActivity?: GitHubActivity }).githubActivity;
  const cached = dataFromActivity(activity, username);

  useEffect(() => {
    let cancelled = false;

    const fetchGitHubData = async () => {
      // Show synced data immediately while live fetch runs
      if (cached && !cancelled) {
        setData(cached);
        setLoading(false);
      }

      try {
        const [userRes, reposRes, contribRes] = await Promise.all([
          fetch(`https://api.github.com/users/${username}`),
          fetch(`https://api.github.com/users/${username}/repos?sort=updated&per_page=100`),
          fetch(`https://github-contributions-api.jogruber.de/v4/${username}?y=last`),
        ]);

        if (!userRes.ok || !reposRes.ok) {
          // Rate-limited or blocked — keep cached data
          if (!cancelled && !cached) setLoading(false);
          return;
        }

        const user: GitHubUser = await userRes.json();
        const repos: GitHubRepo[] = await reposRes.json();
        const repoList = Array.isArray(repos) ? repos : [];
        const totalStars = repoList.reduce((sum, r) => sum + (r.stargazers_count || 0), 0);

        let contributions: ContributionDay[] = [];
        let totalContributions = 0;
        if (contribRes.ok) {
          const contribData = await contribRes.json();
          contributions = contribData.contributions || [];
          totalContributions = contribData.total?.lastYear ?? 0;
        }

        if (cancelled) return;

        setData({
          user,
          repos: repoList.slice(0, 6),
          totalStars,
          contributions,
          totalContributions,
          languageBreakdown: buildLanguageBreakdown(repoList),
          fromCache: false,
        });
      } catch {
        if (!cancelled && !cached) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchGitHubData();
    return () => {
      cancelled = true;
    };
    // cached is derived from setup.json — stable for this session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const syncedLabel = activity?.syncedAt
    ? `Synced ${new Date(activity.syncedAt).toLocaleDateString()}`
    : null;

  return (
    <MotionSection id="github" muted>
      <SectionHeader
        eyebrow="Open Source"
        title="GitHub"
        subtitle="Open source contributions and recent repositories."
      />

      {(setup.github as { privateReposNote?: string }).privateReposNote && (
        <div className="ui-card ui-card-lift p-5 sm:p-6 mb-8 border-l-4 border-l-amber-500/60">
          <p className="text-sm font-semibold text-ink mb-1">Private Repositories</p>
          <p className="text-sm text-ink-muted leading-relaxed">
            {(setup.github as { privateReposNote?: string }).privateReposNote}
          </p>
        </div>
      )}

      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-24 rounded-xl border border-gray-200 dark:border-white/10 animate-pulse bg-gray-100 dark:bg-white/5"
              />
            ))}
          </div>
          <div className="h-40 rounded-xl border border-gray-200 dark:border-white/10 animate-pulse bg-gray-100 dark:bg-white/5" />
        </div>
      ) : !data ? (
        <div className="p-8 rounded-xl border border-dashed border-gray-300 dark:border-white/15 text-center">
          <GitHubIcon sx={{ fontSize: 40 }} className="text-gray-400 mb-3" />
          <p className="text-gray-600 dark:text-gray-400 mb-4">Unable to load GitHub stats right now.</p>
          <p className="text-xs text-ink-muted mb-4">
            GitHub rate limits unauthenticated browser requests. Run{' '}
            <code className="text-ink">npm run sync:github -- --write</code> to refresh local stats.
          </p>
          <a
            href={setup.profile.github}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            <GitHubIcon sx={{ fontSize: 16 }} />
            View profile on GitHub
          </a>
        </div>
      ) : (
        <>
          {data.fromCache && syncedLabel && (
            <p className="text-xs text-ink-muted mb-4">
              Showing synced GitHub data ({syncedLabel}). Live API may be rate-limited.
            </p>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={<FolderOpenIcon sx={{ fontSize: 16 }} />}
              label="Repositories"
              value={data.user.public_repos}
            />
            <StatCard icon={<StarIcon sx={{ fontSize: 16 }} />} label="Total Stars" value={data.totalStars} />
            <StatCard
              icon={<PeopleIcon sx={{ fontSize: 16 }} />}
              label="Followers"
              value={data.user.followers}
            />
            <StatCard
              icon={<CommitIcon sx={{ fontSize: 16 }} />}
              label={data.fromCache ? 'Recent commits' : 'Contributions'}
              value={data.fromCache ? data.totalContributions : `${data.totalContributions}+`}
            />
          </div>

          {data.contributions.length > 0 && (
            <div className="mb-8">
              <ContributionHeatmap contributions={data.contributions} />
            </div>
          )}

          {data.languageBreakdown.length > 0 && (
            <div className="ui-card p-5 sm:p-6 mb-8">
              <p className="ui-eyebrow mb-4">Languages</p>
              <div className="space-y-3">
                {data.languageBreakdown.slice(0, 6).map((lang) => (
                  <div key={lang.language}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-ink">{lang.language}</span>
                      <span className="text-ink-muted tabular-nums">{lang.percent}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-canvas-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-accent transition-all duration-700"
                        style={{ width: `${lang.percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex items-center justify-between mb-4 mt-8">
        <h3 className="text-base font-medium text-ink">Latest Repositories</h3>
        <a
          href={setup.profile.github}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
        >
          <GitHubIcon sx={{ fontSize: 16 }} />
          View Profile
        </a>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-xl border border-gray-200 dark:border-white/10 animate-pulse bg-gray-100 dark:bg-white/5"
            />
          ))}
        </div>
      ) : data && data.repos.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.repos.map((repo, index) => (
            <motion.a
              key={repo.name}
              href={repo.html_url}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="block ui-card ui-card-lift p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <GitHubIcon sx={{ fontSize: 18 }} className="text-gray-500" />
                <span className="font-medium text-gray-900 dark:text-white text-sm">{repo.name}</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3 min-h-[2.5rem]">
                {repo.description || 'No description available.'}
              </p>
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                {repo.language && <span>{repo.language}</span>}
                <span className="flex items-center gap-1">
                  <StarIcon sx={{ fontSize: 14 }} /> {repo.stargazers_count}
                </span>
                <span className="flex items-center gap-1">
                  <ForkRightIcon sx={{ fontSize: 14 }} /> {repo.forks_count}
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      ) : null}
    </MotionSection>
  );
}

export default GitHubSection;
